import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Beer, ClipboardList, LayoutGrid, Users } from 'lucide-react';
import useLandingPages from '../../hooks/useLandingPages';
import { LandingPageSummary, buildFileUrl, landingPath } from '../../services/landingPageService';
import { DEFAULT_LANDING_ICON, LandingIcon } from '../landing/landingIcons';
import ThumbnailImage from '../ThumbnailImage';
import { currentLang } from '../../i18n';
import { t } from '../../i18n';

import ArrowScrollRow from '../../components/common/ArrowScrollRow';
// ============================================
// SolutionsTeaser (2026-09-20, Umbau 2026-09-22) – Startseiten-Block
// „DartCloud für Vereine · Spielstätten · Veranstalter“: Karten (Vereine,
// Spielstätten, Anmeldung, Turniersoftware) mit 16:9-Hero-Bild aus den
// veröffentlichten Landingpages als horizontaler Scroller mit Pfeil-Buttons
// (wie „Aus der Community“). Seit 2026-09-24 (Marcel, SEO): ALLE
// veröffentlichten Lösungen – die vier Kernseiten zuerst in fester Reihenfolge,
// danach alle weiteren nach sortOrder/Titel. Ohne veröffentlichte Seiten
// rendert der Block nichts.
//
// Bild-Regel (Marcel): fester 16:9-Rahmen + object-cover. Geladen wird das
// 16:9-Thumbnail (640×360, seit 22.09. vom Backend erzeugt); fehlt es noch,
// fällt <ThumbnailImage> aufs Original zurück. Nie das quadratische Thumbnail –
// das schnitt im 16:9-Rahmen links/rechts große Teile ab (Live-Befund 22.09.).
// ============================================

export const TEASER_SLUGS: Array<{ slug: string; icon: LandingIcon }> = [
  { slug: 'dartvereine', icon: Users },
  { slug: 'spielstaetten', icon: Beer },
  { slug: 'turnier-anmeldung', icon: ClipboardList },
  { slug: 'turniersoftware', icon: LayoutGrid },
];

/** Kartenbreite in px – Scrollschritt der Pfeile = eine Karte + Abstand */
export const TEASER_CARD_WIDTH = 300;
export const TEASER_CARD_GAP = 12;
export const TEASER_SCROLL_STEP = TEASER_CARD_WIDTH + TEASER_CARD_GAP;

/** ALLE veröffentlichten Lösungen: Kernseiten zuerst (feste Reihenfolge), dann der Rest nach sortOrder/Titel (pure). */
export const pickTeaserPages = (pages: LandingPageSummary[]): Array<LandingPageSummary & { icon: LandingIcon }> => {
  const core = TEASER_SLUGS.map(({ slug, icon }) => {
    const page = pages.find((p) => p.slug === slug);
    return page ? { ...page, icon } : null;
  }).filter((p): p is LandingPageSummary & { icon: LandingIcon } => p !== null);
  const coreSlugs = new Set(core.map((p) => p.slug));
  const rest = pages
    .filter((p) => !coreSlugs.has(p.slug))
    .sort((a, b) => (a.sortOrder - b.sortOrder) || a.title.localeCompare(b.title, 'de'))
    .map((p) => ({ ...p, icon: DEFAULT_LANDING_ICON }));
  return [...core, ...rest];
};

/** Bild-URL für den 16:9-Rahmen: Original statt quadratischem Thumbnail (pure). */
export const teaserImageUrl = (heroImage: string | null | undefined): string | null => buildFileUrl(heroImage);

const SolutionsTeaser: React.FC<{ className?: string }> = ({ className }) => {
  const lang = currentLang();
  const { pages } = useLandingPages(lang);
  const cards = pickTeaserPages(pages);
  if (cards.length === 0) return null;


  return (
    <section aria-label={t('web.component.solutions-teaser.dartcloud-fuer-vereine')} data-testid="solutions-teaser" className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.solutions-teaser.dartcloud-fuer-vereine')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.solutions-teaser.kostenlos-eintragen-turniere')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link
            to={landingPath(null, lang)}
            className="mr-1 flex items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1]"
          >
            {t('web.component.solutions-teaser.alle-loesungen')} <ArrowRight size={13} />
          </Link>
        </div>
      </div>

      <ArrowScrollRow overlay as="ul" testId="solutions-teaser" wrapperTestId="solutions-teaser-rail" step={TEASER_SCROLL_STEP} compact scrollClassName="flex snap-x snap-mandatory gap-3 pb-1">
        {cards.map((card) => {
          const img = teaserImageUrl(card.heroImage);
          const Icon = card.icon;
          return (
            <li key={card.slug} className="w-[300px] shrink-0 snap-start" style={{ width: TEASER_CARD_WIDTH }}>
              <Link
                to={landingPath(card.slug, lang)}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/60 transition hover:border-[#3fced1]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
              >
                <div className="aspect-[16/9] w-full shrink-0 overflow-hidden bg-zinc-900">
                  {img ? (
                    <ThumbnailImage src={img} thumbSize="wide" alt={card.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#3fced1]">
                      <Icon size={26} aria-hidden="true" />
                    </div>
                  )}
                </div>
                <div className="flex min-h-[112px] flex-1 flex-col p-4">
                  <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">{card.title}</h3>
                  {card.subtitle && <p className="mt-1.5 line-clamp-3 text-[13px] leading-relaxed text-gray-400">{card.subtitle}</p>}
                </div>
              </Link>
            </li>
          );
        })}
      </ArrowScrollRow>
    </section>
  );
};

export default SolutionsTeaser;
